import { useState, useEffect, useRef } from 'react'
import { User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const activityIntervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    let mounted = true

    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession()

        if (error) {
          console.error('Error fetching session:', error)
        }

        if (mounted) {
          setUser(session?.user ?? null)
          setLoading(false)

          if (session?.user) {
            await checkAdminStatus()
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
        if (mounted) {
          setLoading(false)
        }
      }
    }

    initAuth()

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      (async () => {
        if (!mounted) return

        setUser(session?.user ?? null)
        setLoading(false)

        if (_event === 'SIGNED_IN' && session?.user) {
          try {
            await supabase.rpc('track_user_login')
            startActivityTracking()
            await checkAdminStatus()
          } catch (error) {
            console.error('Error tracking login:', error)
          }
        } else if (_event === 'SIGNED_OUT') {
          stopActivityTracking()
          setIsAdmin(false)
        }
      })()
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
      stopActivityTracking()
    }
  }, [])

  const startActivityTracking = () => {
    stopActivityTracking()

    const updateActivity = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.access_token) {
          const sessionToken = 'session_' + session.user.id
          await supabase.rpc('update_session_activity', {
            p_session_token: sessionToken
          })
        }
      } catch (error) {
        console.error('Error updating session activity:', error)
      }
    }

    updateActivity()
    activityIntervalRef.current = setInterval(updateActivity, 5 * 60 * 1000)
  }

  const stopActivityTracking = () => {
    if (activityIntervalRef.current) {
      clearInterval(activityIntervalRef.current)
      activityIntervalRef.current = null
    }
  }

  const checkAdminStatus = async () => {
    try {
      const { data, error } = await supabase.rpc('is_admin')
      if (error) {
        console.error('Error checking admin status:', error)
        setIsAdmin(false)
        return
      }
      console.log('Admin status check result:', data)
      setIsAdmin(!!data)
    } catch (error) {
      console.error('Exception checking admin status:', error)
      setIsAdmin(false)
    }
  }

  const signOut = async () => {
    stopActivityTracking()
    setIsAdmin(false)
    await supabase.auth.signOut()
  }

  return { user, loading, signOut, isAdmin }
}